import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toScaleQuestions, type ScaleQuestion, type BilingualText } from '@/lib/firebase/queries/namatScale';
import { saveQuestionerAnswers, type QuestionerAnswer } from '@/lib/firebase/queries/questionersanswers';
import { getAssignedQuestionnaire } from '@/lib/firebase/queries/questionnaireRules';
import type { Questionnaire } from '@/types/questionnaire';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { dispatchScoreUpdate } from './score-events';

interface QuestionnaireProps {
  onComplete: () => void;
  /** The last result when the questionnaire has expired (#94): shown greyed with its date. */
  expired?: { percent: number | null; filledAt: Date };
  /** Closes without saving (#124); without it the overlay cannot be dismissed. */
  onClose?: () => void;
}

export function NamatScaleQuestionnaire({ onComplete, expired, onClose }: QuestionnaireProps) {
  const [questions, setQuestions] = useState<ScaleQuestion[]>([]);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const { user } = useAuth();
  const { t, dir, language } = useLanguage();

  // Helper function to get text in current language
  const getBilingualText = (text: BilingualText | string): string => {
    if (typeof text === 'string') {
      return text;
    }
    return language === 'ar' ? text.ar : text.en;
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        console.log('🔍 Fetching questionnaire questions...');
        if (!user?.uid) return;
        // The questionnaire assigned to this beneficiary by the admin's rules (#92)
        const questionnaireData = await getAssignedQuestionnaire(user.uid);
        const questionsData = toScaleQuestions(questionnaireData);
        console.log('📋 Loaded questions:', questionsData.length, 'questions');
        console.log('📝 Questions data:', questionsData);
        console.log('📋 Loaded questionnaire:', questionnaireData);
        setQuestions(questionsData);
        setQuestionnaire(questionnaireData);
      } catch (error) {
        console.error('❌ Error fetching questions:', error);
        toast.error(t('dashboard.customer.dashboard.questionnaires.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [t, user?.uid]);

  const handleAnswer = (questionId: string, optionId: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
    
    // Auto-advance to next question after a short delay
    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 500); // 500ms delay for smooth transition
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const previousQuestionIndex = currentQuestionIndex - 1;
      
      // Remove answers for the previous question and all questions after it
      setAnswers(prev => {
        const newAnswers = { ...prev };
        
        // Clear from previous question onwards (including current and future)
        for (let i = previousQuestionIndex; i < questions.length; i++) {
          delete newAnswers[questions[i].id];
        }
        
        return newAnswers;
      });
      
      setCurrentQuestionIndex(previousQuestionIndex);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const canGoNext = currentQuestionIndex < questions.length - 1;
  const canGoPrevious = currentQuestionIndex > 0;

  const calculateScore = () => {
    let totalScore = 0;
    let maxPossibleScore = 0;
    const answerDetails: { questionId: string; optionId: string; score: number }[] = [];

    questions.forEach(question => {
      // Find the maximum possible score for this question
      const maxScore = Math.max(...question.options.map(opt => opt.score));
      maxPossibleScore += maxScore;

      const selectedOptionId = answers[question.id];
      if (selectedOptionId) {
        const selectedOption = question.options.find(opt => opt.id === selectedOptionId);
        if (selectedOption) {
          totalScore += selectedOption.score;
          answerDetails.push({
            questionId: question.id,
            optionId: selectedOption.id,
            score: selectedOption.score
          });
        }
      }
    });

    return { 
      totalScore,
      maxPossibleScore,
      answerDetails 
    };
  };

  const handleSubmit = async () => {
    if (!user || !questionnaire) return;

    const { totalScore, maxPossibleScore, answerDetails } = calculateScore();
    
    try {
      setSaving(true);
      
      // Convert answers to new format with additional metadata
      const questionerAnswers: QuestionerAnswer[] = answerDetails.map(answer => {
        const question = questions.find(q => q.id === answer.questionId);
        const option = question?.options.find(o => o.id === answer.optionId);
        
        return {
          questionId: answer.questionId,
          optionId: answer.optionId,
          score: answer.score,
          questionText: question?.question,
          optionText: option?.text,
          category: question?.category
        };
      });
      
      // Save to questionnaire answers collection
      console.log('💾 [NamatScaleQuestionnaire] Saving questionnaire answers:', {
        userId: user.uid,
        questionnaireId: questionnaire.id,
        questionnaireName: questionnaire.name,
        totalScore,
        maxPossibleScore,
        answersCount: questionerAnswers.length
      });
      
      await saveQuestionerAnswers(
        user.uid,
        questionnaire.id,
        questionnaire.name,
        totalScore,
        questionerAnswers,
        maxPossibleScore,
        undefined, // periodId - will use current period
        'customer' // createdBy field to track who completed the questionnaire
      );
      
      // Dispatch custom event with score update
      dispatchScoreUpdate({ score: totalScore, maxScore: maxPossibleScore });

      toast.success(t('dashboard.customer.dashboard.questionnaires.saveSuccess'));
      onComplete();
    } catch (error) {
      console.error('Error saving questionnaire answers:', error);
      toast.error(t('dashboard.customer.dashboard.questionnaires.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const isComplete = questions.length > 0 && 
    questions.every(question => answers[question.id]);

  const closeButton = onClose && (
    <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 shrink-0" data-testid="questionnaire-close">
      <X className="h-4 w-4" />
      <span className="sr-only">{t('common.namatScale.close')}</span>
    </Button>
  );

  if (loading) {
    // In an overlay too: it opens over any dashboard from the top bar (#124).
    return (
      <div className="fixed inset-x-0 top-[56px] bottom-0 bg-[#7dda55]/10 bg-black/20 backdrop-blur-md z-40 flex items-center justify-center p-4" dir={dir}>
        <div className="text-muted-foreground">{t('dashboard.customer.dashboard.questionnaires.loading')}</div>
      </div>
    );
  }

  // If no questions are available, show registration completed message
  if (questions.length === 0) {
    return (
      <div className="fixed inset-x-0 top-[56px] bottom-0 bg-[#7dda55]/10 bg-black/20 backdrop-blur-md z-40 flex items-center justify-center p-4 overflow-y-auto" dir={dir}>
        <Card className="w-full max-w-2xl shadow-lg">
          <div className="p-6 space-y-6 text-center">
            {closeButton && <div className="flex justify-end">{closeButton}</div>}
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-green-600">
                {t('dashboard.customer.dashboard.questionnaires.registrationCompleted')}
              </h2>
              <p className="text-muted-foreground">
                                  {t('dashboard.customer.dashboard.questionnaires.registrationCompletedDescription')}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 top-[56px] bottom-0 bg-[#7dda55]/10 bg-black/20 backdrop-blur-md z-40 flex items-center justify-center p-4 overflow-y-auto" dir={dir}>
      <Card className="w-full max-w-2xl shadow-lg">
        <div className="p-6 space-y-6">
          {/* Header with progress */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{t('dashboard.customer.dashboard.questionnaires.title')}</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {currentQuestionIndex + 1} / {questions.length}
                </span>
                {closeButton}
              </div>
            </div>
            <Progress 
              value={((currentQuestionIndex + 1) / questions.length) * 100} 
              className="h-2"
            />
            <p className="text-muted-foreground">{t('dashboard.customer.dashboard.questionnaires.description')}</p>
            {expired && (
              <div className="rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground" data-testid="questionnaire-expired">
                {t('dashboard.customer.dashboard.questionnaires.expired', {
                  percent: expired.percent ?? '—',
                  date: expired.filledAt.toLocaleDateString(language === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-GB'),
                })}
              </div>
            )}
          </div>

          {/* Current Question */}
          {currentQuestion && (
            <div className="space-y-6 min-h-[300px] flex flex-col">
              <div className="space-y-4 flex-1">
                <Label className="text-lg font-medium">
                  {currentQuestionIndex + 1}. {getBilingualText(currentQuestion.question)}
                </Label>
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
                  className="space-y-3"
                >
                  {currentQuestion.options.filter(option => !option.hidden).map((option) => (
                    <div key={option.id} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value={option.id} id={`${currentQuestion.id}-${option.id}`} />
                      <Label htmlFor={`${currentQuestion.id}-${option.id}`} className="flex-1 cursor-pointer">
                        {getBilingualText(option.text)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={!canGoPrevious}
                  className="flex items-center gap-2"
                >
                  {dir === 'rtl' ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                  {t('education.previous')}
                </Button>

                {/* Only show Submit button on last question */}
                {!canGoNext && (
                  <Button
                    onClick={handleSubmit}
                    disabled={!isComplete || saving}
                    className="bg-[#7dda55] hover:bg-[#6dc447] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? t('dashboard.customer.dashboard.questionnaires.saving') : t('dashboard.customer.dashboard.questionnaires.submit')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
} 
